import { FORM_NAME } from 'app/content/constants';
import { getContentId } from 'app/content/selectors';
import { changeField } from 'app/schema-form/actions';
import { getRouter } from 'app/selectors';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getRoutineSaga } from '../common/utils/new-sagas';
import {
  CONTENT_ARCHIVED,
  type ContentArchivedType,
  GET_NEXT_VERSION_LIST,
  getContentVersionRoutine,
  getVersionListRoutine,
  RESTORE_VERSION_CONTENT,
  type VersionListRequest,
  VIEW_CONTENT_DIFF,
  type ViewContentDiffType
} from './actions';
import { getContentVersion, getContentVersionList } from './api';
import { initialContentVersionData } from './reducer';
import { type ContentDiff, getContentDiff, getSelectedVersionFormContent, getVersionList } from './selectors';
import type { Version } from './types';

export const PAGINATION_LIMIT = 100;

export function* getVersionListSaga(params: VersionListRequest): SagaIterator {
  const { contentId, from, size } = params;

  let currentItems = yield select(getVersionList);

  // Clear old items in case we've switched content id
  currentItems = currentItems.filter((item: Version) => {
    return item.id === contentId;
  });

  const { result } = yield call(getContentVersionList, contentId, from, size);

  return {
    ...result,
    items: orderBy(uniqBy(currentItems.concat(result.items), 'version'), 'version', ['desc'])
  };
}

export function* getNextVersionListSaga(): SagaIterator {
  const { params } = yield select(getRouter);
  const contentId = yield select(getContentId, params);
  const currentItems = yield select(getVersionList);
  yield put(
    getVersionListRoutine.requested({
      contentId,
      from: currentItems.length,
      size: PAGINATION_LIMIT
    })
  );
}

export function* getContentVersionSaga(payload: number | null): SagaIterator {
  if (payload) {
    const { params, query } = yield select(getRouter);
    const contentId = yield select(getContentId, params);
    const { result } = yield call(getContentVersion, contentId, payload, query.locale || '', true);
    return result;
  }

  return initialContentVersionData;
}

export function* handleContentVersion({ payload }: ViewContentDiffType): SagaIterator {
  yield put(getContentVersionRoutine.requested(payload));
}

export function* restoreVersionContentSaga(): SagaIterator {
  const versionContent = yield select(getSelectedVersionFormContent);
  const contentDiff = yield select(getContentDiff);
  yield all(
    contentDiff.map(
      (value: ContentDiff) =>
        value.isRestorable &&
        versionContent.get(value.key) &&
        put(changeField(FORM_NAME, value.key)(versionContent.get(value.key)))
    )
  );
}

export function* handleContentArchived(action: ContentArchivedType): SagaIterator {
  const { params } = yield select(getRouter);
  if (params.contentId && params.shapeName) {
    const contentId = yield select(getContentId, params);
    if (action.payload.contentId === contentId) {
      yield put(getVersionListRoutine.requested({ contentId, from: 0, size: 4 }));
    }
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(getVersionListRoutine.REQUESTED, getRoutineSaga(getVersionListRoutine, getVersionListSaga)),
    takeLatest(getContentVersionRoutine.REQUESTED, getRoutineSaga(getContentVersionRoutine, getContentVersionSaga)),
    takeLatest(VIEW_CONTENT_DIFF, handleContentVersion),
    takeLatest(RESTORE_VERSION_CONTENT, restoreVersionContentSaga),
    takeLatest(CONTENT_ARCHIVED, handleContentArchived),
    takeLatest(GET_NEXT_VERSION_LIST, getNextVersionListSaga)
  ]);
}
